// src/encounter/ap-visit-complaint.tsx
//
// ApVisitComplaint · Signal level: block · source: the encounter's complaint document
// (GET /api/Patients/GetPatientComplaint, the SAME read the HPI block draws).
//
// The visit's chief complaint, READ in the Assessment & Plan step. Behind `?ef=frameV2` only (the
// caller gates it); OFF keeps today's A&P DOM unchanged.
//
// WHY IT EXISTS. Reported on the dev test walk (1 Oct): "chief complaint is not moving to
// assessment". The complaint was charted (HPI drew its card, the ambient record said "written to
// chart"), but the A&P step read nothing from it, so the step where the physician decides showed no
// trace of why the patient came. The approved design keeps the visit's reason and concerns in view
// while the Assessment is written (R6c Encounter Frame: Subjective "Chief complaint" chips; the
// Patient Workspace header "Reason for visit" on every section).
//
// RULES HELD HERE (encounter authority §4 rules 3 and 4):
//   · A READOUT. It names the complaint and offers no verb. It never adds, pre-fills or suggests a
//     diagnosis: whether a complaint becomes a coded sign or symptom in the diagnoses block is the
//     physician's act, through the A&P's own search, exactly as before.
//   · One source. The list is the complaint document the HPI block and the sign gate read, so the
//     two steps cannot disagree.
//   · Honest states (contract §1a definitions): nothing while the read is in flight; ONE line in
//     words on a failed read (never "no complaint"); one compact line when the read succeeded and
//     nothing is recorded, because that absence is a fact the sign gate also acts on.
import { cn } from '@scripsteam/scrips-signal-ds';
import { V2_BODY_QUIET, V2_EYEBROW } from './lib/design-v2';

/** The complaint chip (R6c S block): tokens only. Exported so the test pins the look. */
export const COMPLAINT_CHIP =
  'inline-flex items-center h-8 px-3 rounded-full border border-solid border-border-strong bg-surface-card text-size-sm font-semibold leading-none text-text-primary whitespace-nowrap';

/** What the A&P renders the complaint from. `ready` with `[]` is the successful-empty read. */
export type VisitComplaintState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; complaints: readonly string[] };

export const VISIT_COMPLAINT_LABEL = 'Chief complaint';
export const VISIT_COMPLAINT_NONE = 'No chief complaint recorded for this visit yet.';
export const VISIT_COMPLAINT_ERROR = "Chief complaint couldn't be loaded.";

/** The complaint names to show: trimmed, blanks dropped, first occurrence kept, order preserved. */
function visitComplaintNames(complaints: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of complaints) {
    const name = raw.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

/** Presentational: renders exactly what `state` says. */
export function ApVisitComplaint({ state, className }: { state: VisitComplaintState; className?: string }) {
  if (state.kind === 'loading') return null;
  if (state.kind === 'error') {
    return <p className={cn('m-0', V2_BODY_QUIET, className)}>{VISIT_COMPLAINT_ERROR}</p>;
  }
  const names = visitComplaintNames(state.complaints);
  if (names.length === 0) {
    // The successful-empty read: ONE compact line, no label row above it (no empty box, UC-26).
    return (
      <section aria-label={VISIT_COMPLAINT_LABEL} data-block="ChiefComplaint" className={className}>
        <p className={cn('m-0', V2_BODY_QUIET)}>{VISIT_COMPLAINT_NONE}</p>
      </section>
    );
  }
  return (
    <section aria-label={VISIT_COMPLAINT_LABEL} data-block="ChiefComplaint" className={cn('flex flex-col gap-1', className)}>
      <span className={V2_EYEBROW}>{VISIT_COMPLAINT_LABEL}</span>
      {/* The SAME chip the S section draws for the complaint (R6c Encounter Frame, S block
          kind 'chips', "Chief complaint"): 28 tall, full pill, 1px border-strong on the card
          surface, 600 13 text-primary, nowrap. Read-only: no remove, no add (design desk, #571:
          "one clinical thing gets one look everywhere in the encounter"). GAP: no DS read-only
          complaint chip exists (ChiefComplaintKeywordChip is the selectable keyword tag), so it is
          composed from tokens here until the DS graduates one. */}
      <ul className="m-0 p-0 list-none flex flex-wrap gap-2">
        {names.map((name) => (
          <li key={name} data-slot="complaint-chip" className={COMPLAINT_CHIP}>
            {name}
          </li>
        ))}
      </ul>
    </section>
  );
}
